/* eslint-disable max-classes-per-file */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';

export class NavigationPage {
  static Home = 'home';
  static Collection = 'collection';
  static Object = 'object';
  static Search = 'search';
  static Order = 'order';
  static Blank = 'Blank';
}

// Auxiliary classes to hold some data

type CatalogObjectCaller = {
  page : string;
  collection : string;
  filter: NavigationReducerDataFilter | undefined;
  searchQuery: string;
};

export class NavigationReducerDataFilterTags {
  type : string = '';
  value : string = '';
}

export class NavigationReducerDataFilter {
  name : string = '';
  tags : NavigationReducerDataFilterTags[] = [];
}

// Class that represents the data of this store

class NavigationReducerData {

  static SaveCatalogObjectCallerInfo(state : NavigationReducerData) :
  NavigationReducerData | undefined {

    const newState = _.cloneDeep(state);

    const newCatalogObjectCaller : CatalogObjectCaller = {
      page: state.page,
      collection: state.collection,
      filter: _.cloneDeep(state.filter),
      searchQuery: state.searchQuery ?? '',
    };

    newState.catalogObjectCaller = newCatalogObjectCaller;

    return newState;
  }

  static RestoreCatalogObjectCallerInfo(state : NavigationReducerData) :
  NavigationReducerData | undefined {

    const newState = _.cloneDeep(state);

    if (state.catalogObjectCaller === undefined) {
      return newState;
    }

    // Copy the saved data to the main location
    newState.page = state.catalogObjectCaller.page;
    newState.collection = state.catalogObjectCaller.collection;
    newState.filter = _.cloneDeep(state.catalogObjectCaller.filter);
    newState.searchQuery = state.catalogObjectCaller.searchQuery;

    // Clear the saved data
    newState.catalogObjectCaller = undefined;

    return newState;
  }

  page : string = '';
  collection : string = '';
  object : string = '';
  filter : NavigationReducerDataFilter | undefined;
  searchQuery : string | undefined = undefined;
  catalogObjectCaller : CatalogObjectCaller | undefined;
}

// Initial State

const initialState : NavigationReducerData = {
  page: '',
  collection: '',
  object: '',
  filter: undefined,
  searchQuery: undefined,
  catalogObjectCaller: undefined,
};

// Store definition

export const NavigationReducer = createSlice({

  name: 'NavigationReducer',

  initialState,

  reducers: {

    changeState: (state, action : PayloadAction<
    {
      page : string,
      collection : string,
      object : string,
      filter : NavigationReducerDataFilter | undefined,
      searchQuery : string | undefined,
    }>) : NavigationReducerData | undefined => {

      let newState : NavigationReducerData | undefined;

      const navigatingToObject = action.payload.page === NavigationPage.Object;

      if (navigatingToObject) {
        // Create a new state where the current state is saved
        newState = NavigationReducerData.SaveCatalogObjectCallerInfo(state);
      } else {
        // Create a new state
        newState = _.cloneDeep(state);
      }

      if (newState === undefined) {
        return undefined;
      }

      // Update the navigation properties
      newState.page = action.payload.page;
      newState.collection = action.payload.collection;
      newState.object = action.payload.object;
      newState.filter = _.cloneDeep(action.payload.filter);
      newState.searchQuery = action.payload.searchQuery;

      // Return the new state
      return newState;
    },

    updateFilterTags: (state, action : PayloadAction<
    {
      filters : NavigationReducerDataFilterTags[]
    }>) => {

      // Create a new state
      const newState = _.cloneDeep(state);

      if (newState === undefined) {
        return undefined;
      }

      if (newState.filter === undefined) {
        newState.filter = new NavigationReducerDataFilter();
      }

      // Update the filters
      newState.filter.tags = _.cloneDeep(action.payload.filters);

      // Return the new state
      return newState;
    },

    restoreCatalogObjectCallerInfo: (state) => {

      // Restore the location
      const newState = NavigationReducerData.RestoreCatalogObjectCallerInfo(state);

      // Return the new state
      return newState;
    },
  },
});
